import { observer } from "mobx-react-lite";
import { useState, useRef } from "react";
import {
  MDSMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSButton,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSSelectMenu,
  MDSSelectMenuItem,
  MDSMenuItemSelectionVariant,
  MDSMenuInputHeader,
  MDSButtonSlotName,
  MDSCollectionIcon,
} from "@/mds";
import type { FC } from "react";

interface CollectionSelectorDropdownProps {
  /**
   * Button label when no specific items are selected
   */
  label: string;

  /**
   * Filtered items to display (already filtered by search query)
   */
  items: string[];

  /**
   * Currently selected item names
   */
  selectedItems: string[];

  /**
   * Callback when selection changes
   */
  onSelectionChange: (selectedItems: string[]) => void;

  /**
   * Whether the dropdown is disabled (when parent toggle is off)
   */
  isDisabled: boolean;

  /**
   * Function to get collection ID for an item name
   */
  getCollectionId: (itemName: string) => string | undefined;

  /**
   * Function to get the count of notes for an item
   */
  getNotesCount: (itemName: string) => number;

  /**
   * Current search query value
   */
  searchQuery: string;

  /**
   * Callback when search query changes
   */
  onSearchQueryChange: (query: string) => void;
}

export const CollectionSelectorDropdown: FC<CollectionSelectorDropdownProps> = observer(
  function CollectionSelectorDropdown({
    label,
    items,
    selectedItems,
    onSelectionChange,
    isDisabled,
    getCollectionId,
    getNotesCount,
    searchQuery,
    onSearchQueryChange,
  }) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownListRef = useRef<HTMLDivElement>(null);

    const handleToggleItem = (itemName: string) => {
      const isSelected = selectedItems.includes(itemName);
      if (isSelected) {
        onSelectionChange(selectedItems.filter(name => name !== itemName));
      } else {
        onSelectionChange([...selectedItems, itemName]);
      }
    };

    return (
      <MDSMenuDropdown isOpen={isDisabled ? false : isOpen} onOpenChange={setIsOpen}>
        <MDSButton
          slot={MDSMenuDropdownSlotName.Trigger}
          variant={MDSButtonVariant.Transparent}
          isDisabled={isDisabled}
        >
          {label}
          {isOpen ? (
            <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.AngleUp} />
          ) : (
            <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.AngleDown} />
          )}
        </MDSButton>
        <div slot={MDSMenuDropdownSlotName.Menu}>
          <MDSMenuInputHeader
            onChange={onSearchQueryChange}
            onClear={() => onSearchQueryChange("")}
            value={searchQuery}
            onKeyDirection={() => {
              if (dropdownListRef.current) dropdownListRef.current.focus();
            }}
            onEscape={() => {
              if (searchQuery) onSearchQueryChange("");
              else dropdownListRef.current?.focus();
            }}
            placeholder="Filter..."
          />
          <MDSSelectMenu
            ref={dropdownListRef}
            aria-label={label}
            selectionBehavior="toggle"
            selectionMode="single"
            shouldFocusWrap={true}
            style={{ maxHeight: "50vh", maxWidth: 320 }}
          >
            {items.length > 0 ? (
              items.map(item => {
                const collectionId = getCollectionId(item);
                const notesCount = getNotesCount(item);
                return (
                  <MDSSelectMenuItem
                    key={item}
                    id={item}
                    label={item}
                    subtitle={`in ${notesCount} ${notesCount === 1 ? "note" : "notes"}`}
                    icon={() => <MDSCollectionIcon collectionId={collectionId} size={11} />}
                    onClick={() => handleToggleItem(item)}
                    isSelected={selectedItems.includes(item)}
                    selectionVariant={MDSMenuItemSelectionVariant.RadioButton}
                  />
                );
              })
            ) : (
              <MDSSelectMenuItem
                key="empty-state"
                id="empty-state"
                label="No items found"
                isDisabled={true}
              />
            )}
          </MDSSelectMenu>
        </div>
      </MDSMenuDropdown>
    );
  }
);
